import React, { useState } from "react";
import {
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  InputAdornment,
  Divider,
  Chip,
} from "@mui/material";
import {
  FileText,
  Plus,
  Edit,
  Trash,
  AlertCircle,
  Percent,
  MapPin,
  Building,
} from "lucide-react";
import ProductSection from "../../ProductsModule/Components/ProductSection";

const TaxSettings = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTaxRate, setCurrentTaxRate] = useState(null);
  const [enableTax, setEnableTax] = useState(true);
  const [showPricesWithTax, setShowPricesWithTax] = useState(true);
  const [automaticTaxCalculation, setAutomaticTaxCalculation] = useState(true);

  // Sample tax rates
  const [taxRates, setTaxRates] = useState([
    {
      id: 1,
      name: "GST 18%",
      rate: 18,
      type: "GST",
      country: "India",
      isDefault: true,
    },
    {
      id: 2,
      name: "GST 12%",
      rate: 12,
      type: "GST",
      country: "India",
      isDefault: false,
    },
    {
      id: 3,
      name: "GST 5%",
      rate: 5,
      type: "GST",
      country: "India",
      isDefault: false,
    },
    {
      id: 4,
      name: "GST 28%",
      rate: 28,
      type: "GST",
      country: "India",
      isDefault: false,
    },
  ]);

  // Business information
  const [businessInfo, setBusinessInfo] = useState({
    gstNumber: "27AABCT3518Q1ZV",
    panNumber: "AABCT3518Q",
    businessType: "registered",
    taxationMethod: "normal",
  });

  const handleAddTaxRate = () => {
    setIsEditing(false);
    setCurrentTaxRate({
      id: null,
      name: "",
      rate: "",
      type: "GST",
      country: "India",
      isDefault: false,
    });
    setOpenDialog(true);
  };

  const handleEditTaxRate = taxRate => {
    setIsEditing(true);
    setCurrentTaxRate({ ...taxRate });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentTaxRate(null);
  };

  const handleDeleteTaxRate = id => {
    setTaxRates(taxRates.filter(taxRate => taxRate.id !== id));
  };

  const handleSaveTaxRate = () => {
    if (isEditing) {
      setTaxRates(
        taxRates.map(taxRate =>
          taxRate.id === currentTaxRate.id ? currentTaxRate : taxRate,
        ),
      );
    } else {
      setTaxRates([
        ...taxRates,
        {
          ...currentTaxRate,
          id:
            taxRates.length > 0 ? Math.max(...taxRates.map(t => t.id)) + 1 : 1,
        },
      ]);
    }
    setOpenDialog(false);
  };

  return (
    <div className="space-y-8">
      {/* General Tax Settings */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex items-center mb-4">
          <FileText size={20} className="text-[#5652B0] mr-2" />
          <ProductSection data={"General Tax Settings"} />
        </div>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, fontFamily: "Outfit" }}>
          Configure how taxes are applied across your system
        </Typography>

        <div className="space-y-3">
          <FormControlLabel
            control={
              <Switch
                checked={enableTax}
                onChange={e => setEnableTax(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#5652B0",
                    "&:hover": {
                      backgroundColor: "rgba(86, 82, 176, 0.08)",
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#5652B0",
                  },
                }}
              />
            }
            label={
              <div>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: "Outfit", fontWeight: 500 }}>
                  Enable Tax Calculation
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: "Outfit" }}>
                  Apply tax calculations on products and services
                </Typography>
              </div>
            }
            sx={{ display: "flex", alignItems: "flex-start", marginLeft: 0 }}
          />

          <Divider />

          <FormControlLabel
            control={
              <Switch
                checked={showPricesWithTax}
                onChange={e => setShowPricesWithTax(e.target.checked)}
                disabled={!enableTax}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#5652B0",
                    "&:hover": {
                      backgroundColor: "rgba(86, 82, 176, 0.08)",
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#5652B0",
                  },
                }}
              />
            }
            label={
              <div>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: "Outfit", fontWeight: 500 }}>
                  Display Prices with Tax
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: "Outfit" }}>
                  Show product prices inclusive of taxes
                </Typography>
              </div>
            }
            sx={{ display: "flex", alignItems: "flex-start", marginLeft: 0 }}
          />

          <Divider />

          <FormControlLabel
            control={
              <Switch
                checked={automaticTaxCalculation}
                onChange={e => setAutomaticTaxCalculation(e.target.checked)}
                disabled={!enableTax}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#5652B0",
                    "&:hover": {
                      backgroundColor: "rgba(86, 82, 176, 0.08)",
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#5652B0",
                  },
                }}
              />
            }
            label={
              <div>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: "Outfit", fontWeight: 500 }}>
                  Automatic Tax Calculation
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: "Outfit" }}>
                  Apply taxes automatically based on product category and
                  location
                </Typography>
              </div>
            }
            sx={{ display: "flex", alignItems: "flex-start", marginLeft: 0 }}
          />
        </div>
      </div>

      {/* Business Information */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex items-center mb-4">
          <Building size={20} className="text-[#5652B0] mr-2" />
          <ProductSection data={"Business Information"} />
        </div>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, fontFamily: "Outfit" }}>
          Update your business tax registration information
        </Typography>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextField
            label="GST Registration Number"
            variant="outlined"
            size="small"
            fullWidth
            value={businessInfo.gstNumber}
            onChange={e =>
              setBusinessInfo({ ...businessInfo, gstNumber: e.target.value })
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#5652B0",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#5652B0",
              },
            }}
          />

          <TextField
            label="PAN Number"
            variant="outlined"
            size="small"
            fullWidth
            value={businessInfo.panNumber}
            onChange={e =>
              setBusinessInfo({ ...businessInfo, panNumber: e.target.value })
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#5652B0",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#5652B0",
              },
            }}
          />

          <FormControl
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#5652B0",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#5652B0",
              },
            }}>
            <InputLabel id="business-type-label">Business Type</InputLabel>
            <Select
              labelId="business-type-label"
              id="business-type"
              label="Business Type"
              value={businessInfo.businessType}
              onChange={e =>
                setBusinessInfo({
                  ...businessInfo,
                  businessType: e.target.value,
                })
              }>
              <MenuItem value="registered">Registered Business</MenuItem>
              <MenuItem value="unregistered">Unregistered Business</MenuItem>
              <MenuItem value="composition">Composition Scheme</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#5652B0",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#5652B0",
              },
            }}>
            <InputLabel id="taxation-method-label">Taxation Method</InputLabel>
            <Select
              labelId="taxation-method-label"
              id="taxation-method"
              label="Taxation Method"
              value={businessInfo.taxationMethod}
              onChange={e =>
                setBusinessInfo({
                  ...businessInfo,
                  taxationMethod: e.target.value,
                })
              }>
              <MenuItem value="normal">Normal Taxation</MenuItem>
              <MenuItem value="composition">Composition Scheme</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Tax Rates */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <Percent size={20} className="text-[#5652B0] mr-2" />
            <ProductSection data={"Tax Rates"} />
          </div>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            size="small"
            disabled={!enableTax}
            onClick={handleAddTaxRate}
            sx={{
              backgroundColor: "#5652B0",
              color: "white",
              textTransform: "none",
              fontFamily: "Outfit",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            Add Tax Rate
          </Button>
        </div>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, fontFamily: "Outfit" }}>
          Configure tax rates for different product categories and regions
        </Typography>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", border: "1px solid #E5E7EB" }}>
          <Table aria-label="tax rates table">
            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Rate
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Type
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Country
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {taxRates.map(taxRate => (
                <TableRow key={taxRate.id}>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 500 }}>
                    {taxRate.name}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit" }}>
                    {taxRate.rate}%
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit" }}>
                    {taxRate.type}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit" }}>
                    {taxRate.country}
                  </TableCell>
                  <TableCell>
                    {taxRate.isDefault && (
                      <Chip
                        label="Default"
                        size="small"
                        sx={{
                          backgroundColor: "#F1F0FF",
                          color: "#5652B0",
                          fontFamily: "Outfit",
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditTaxRate(taxRate)}
                      sx={{
                        color: "#6B7280",
                        "&:hover": {
                          backgroundColor: "rgba(107, 114, 128, 0.08)",
                        },
                        mr: 1,
                      }}>
                      <Edit size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTaxRate(taxRate.id)}
                      disabled={taxRate.isDefault}
                      sx={{
                        color: "#EF4444",
                        "&:hover": {
                          backgroundColor: "rgba(239, 68, 68, 0.08)",
                        },
                      }}>
                      <Trash size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {taxRates.length === 0 && (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <AlertCircle size={48} className="text-gray-300 mb-4" />
            <Typography
              variant="h6"
              sx={{ fontFamily: "Outfit", color: "text.secondary", mb: 1 }}>
              No Tax Rates Defined
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: "Outfit", color: "text.secondary", mb: 3 }}>
              You haven't defined any tax rates yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleAddTaxRate}
              sx={{
                backgroundColor: "#5652B0",
                color: "white",
                textTransform: "none",
                fontFamily: "Outfit",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
              }}>
              Add Tax Rate
            </Button>
          </div>
        )}
      </div>

      {/* Regional Tax Settings */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex items-center mb-4">
          <MapPin size={20} className="text-[#5652B0] mr-2" />
          <ProductSection data={"Regional Tax Settings"} />
        </div>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, fontFamily: "Outfit" }}>
          Configure tax settings for different regions
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: "Outfit", fontWeight: 600, mb: 2 }}>
            Default Tax Region
          </Typography>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}>
              <InputLabel id="country-label">Country</InputLabel>
              <Select
                labelId="country-label"
                id="country"
                label="Country"
                defaultValue="IN">
                <MenuItem value="IN">India</MenuItem>
                <MenuItem value="US">United States</MenuItem>
                <MenuItem value="UK">United Kingdom</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}>
              <InputLabel id="state-label">State</InputLabel>
              <Select
                labelId="state-label"
                id="state"
                label="State"
                defaultValue="MH">
                <MenuItem value="MH">Maharashtra</MenuItem>
                <MenuItem value="DL">Delhi</MenuItem>
                <MenuItem value="TN">Tamil Nadu</MenuItem>
                <MenuItem value="KA">Karnataka</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Postal Code"
              variant="outlined"
              size="small"
              fullWidth
              defaultValue="400001"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </div>
        </Box>

        <FormControlLabel
          control={
            <Switch
              defaultChecked={true}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#5652B0",
                  "&:hover": {
                    backgroundColor: "rgba(86, 82, 176, 0.08)",
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#5652B0",
                },
              }}
            />
          }
          label={
            <div>
              <Typography
                variant="body1"
                sx={{ fontFamily: "Outfit", fontWeight: 500 }}>
                Calculate Tax Based on Shipping Address
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Outfit" }}>
                Apply taxes based on the customer's shipping address
              </Typography>
            </div>
          }
          sx={{ display: "flex", alignItems: "flex-start", marginLeft: 0 }}
        />
      </div>

      {/* Add/Edit Tax Rate Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
          {isEditing ? "Edit Tax Rate" : "Add Tax Rate"}
        </DialogTitle>
        <DialogContent dividers>
          <div className="space-y-4 py-2">
            <TextField
              label="Name"
              variant="outlined"
              size="small"
              fullWidth
              value={currentTaxRate?.name || ""}
              onChange={e =>
                setCurrentTaxRate({ ...currentTaxRate, name: e.target.value })
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />

            <TextField
              label="Rate"
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              value={currentTaxRate?.rate || ""}
              onChange={e =>
                setCurrentTaxRate({ ...currentTaxRate, rate: e.target.value })
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />

            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}>
              <InputLabel id="tax-type-label">Tax Type</InputLabel>
              <Select
                labelId="tax-type-label"
                id="tax-type"
                label="Tax Type"
                value={currentTaxRate?.type || "GST"}
                onChange={e =>
                  setCurrentTaxRate({ ...currentTaxRate, type: e.target.value })
                }>
                <MenuItem value="GST">GST</MenuItem>
                <MenuItem value="IGST">IGST</MenuItem>
                <MenuItem value="CGST">CGST</MenuItem>
                <MenuItem value="SGST">SGST</MenuItem>
                <MenuItem value="VAT">VAT</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}>
              <InputLabel id="country-label">Country</InputLabel>
              <Select
                labelId="country-label"
                id="country"
                label="Country"
                value={currentTaxRate?.country || "India"}
                onChange={e =>
                  setCurrentTaxRate({
                    ...currentTaxRate,
                    country: e.target.value,
                  })
                }>
                <MenuItem value="India">India</MenuItem>
                <MenuItem value="United States">United States</MenuItem>
                <MenuItem value="United Kingdom">United Kingdom</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={currentTaxRate?.isDefault || false}
                  onChange={e =>
                    setCurrentTaxRate({
                      ...currentTaxRate,
                      isDefault: e.target.checked,
                    })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#5652B0",
                      "&:hover": {
                        backgroundColor: "rgba(86, 82, 176, 0.08)",
                      },
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#5652B0",
                    },
                  }}
                />
              }
              label="Set as default tax rate"
              sx={{ marginLeft: 0 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: "#6B7280",
              textTransform: "none",
              fontFamily: "Outfit",
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTaxRate}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              color: "white",
              textTransform: "none",
              fontFamily: "Outfit",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            {isEditing ? "Save Changes" : "Add Tax Rate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 mt-6">
        <Button
          variant="outlined"
          sx={{
            borderColor: "#5652B0",
            color: "#5652B0",
            textTransform: "none",
            fontFamily: "Outfit",
            "&:hover": {
              borderColor: "#4A47A3",
              backgroundColor: "rgba(86, 82, 176, 0.04)",
            },
          }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#5652B0",
            color: "white",
            textTransform: "none",
            fontFamily: "Outfit",
            "&:hover": {
              backgroundColor: "#4A47A3",
            },
          }}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default TaxSettings;
